// Basiskarte der Kartenseite: Landflaechen, Grenzen, Seen und
// Beschriftungen. Erzeugt von data/build/basiskarte.mjs, ausgeliefert als
// statische Datei unter /karte/basiskarte.json.
//
// Warum eigene Geometrie statt Kartenkacheln: Die Karte hat keinen
// Kachel-Anbieter (Projektplan Paragraf 10.1; die Sicherheitskoepfe in
// deploy/nginx erlauben mit "img-src 'self'" auch gar keinen). Ohne diese
// Datei stuenden die Kreise auf leerem Grund - kein Meer, keine
// Nachbarlaender, keine Ortsnamen, anders als in der Vorlage
// (docs/reference/IMG_4900.png).

import { useQuery } from "@tanstack/react-query";
import type { FeatureCollection } from "geojson";

/** Ein Name auf der Karte: Land, Ort oder Meer. */
export interface Beschriftung {
  name: string;
  lng: number;
  lat: number;
  /** Ab dieser Zoomstufe sichtbar (aus Natural Earth uebernommen). */
  abZoom: number;
  /** Bis zu dieser Zoomstufe sichtbar; Orte haben keine Obergrenze. */
  bisZoom?: number;
  hauptstadt?: boolean;
}

export interface Basiskarte {
  land: FeatureCollection;
  binnengrenzen: FeatureCollection;
  seen: FeatureCollection;
  fluesse: FeatureCollection;
  strassen: FeatureCollection;
  laenderNamen: Beschriftung[];
  orte: Beschriftung[];
  meere: Beschriftung[];
}

export function useBasiskarte() {
  return useQuery({
    queryKey: ["basiskarte"],
    // Aendert sich nur, wenn ein neuer Build ausgerollt wird.
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: async (): Promise<Basiskarte> => {
      const antwort = await fetch(`${import.meta.env.BASE_URL}karte/basiskarte.json`);
      if (!antwort.ok) throw new Error(`Basiskarte: HTTP ${antwort.status}`);
      return (await antwort.json()) as Basiskarte;
    },
  });
}

/**
 * Natural Earth staffelt feiner, als Leaflet zoomt (6,1 fuer Goettingen).
 *
 * Nach unten stand hier bis zum 11.09.2026 eine Null: ohne sie lagen die
 * Namen uebereinander, und lieber zu wenige als ein Knaeuel. Seit
 * ohneUeberdeckung() aussortiert, was sich ins Gehege kommt, darf wieder
 * ein ganzer Schritt Nachsicht sein - die Vorlage zeigt bei Zoom 6 ueber
 * Grossbritannien auch Wick, Leeds und Cardiff, und wer Kennzeichen
 * sammelt, sucht genau solche Orte.
 *
 * Nach oben war die Nachsicht schon immer sinnvoll: sonst verschwindet
 * "DEUTSCHLAND" schon einen Schritt, nachdem man hineingezoomt hat.
 */
const TOLERANZ_UNTEN = 1;
const TOLERANZ_OBEN = 1;

/**
 * Welche Namen bei dieser Zoomstufe und in diesem Ausschnitt gezeichnet
 * werden. `hoechstens` deckelt die Zahl: bei weit herausgezogener Karte
 * liegen sonst tausend Ortsnamen uebereinander, und jeder davon ist ein
 * eigenes DOM-Element.
 *
 * Die Liste muss nach `abZoom` aufsteigend sortiert sein (so liefert der
 * Generator sie), damit beim Deckeln die wichtigsten Namen uebrig
 * bleiben.
 */
export function sichtbareBeschriftungen(
  beschriftungen: Beschriftung[],
  zoom: number,
  imAusschnitt: (beschriftung: Beschriftung) => boolean,
  hoechstens: number,
): Beschriftung[] {
  const treffer: Beschriftung[] = [];
  for (const beschriftung of beschriftungen) {
    if (beschriftung.abZoom > zoom + TOLERANZ_UNTEN) continue;
    if (beschriftung.bisZoom !== undefined && zoom > beschriftung.bisZoom + TOLERANZ_OBEN) continue;
    if (!imAusschnitt(beschriftung)) continue;
    treffer.push(beschriftung);
    if (treffer.length >= hoechstens) break;
  }
  return treffer;
}

/** Ein Name mit dem Platz, den er auf dem Bildschirm braucht. */
export interface Namenskasten {
  schluessel: string;
  /** Linke Kante in Bildpunkten. */
  x: number;
  /** Obere Kante in Bildpunkten. */
  y: number;
  breite: number;
  hoehe: number;
}

/**
 * Welche Namen ohne Ueberdeckung nebeneinander passen. Die Liste kommt in
 * der Reihenfolge ihrer Wichtigkeit herein; wer mit einem schon
 * angenommenen Kasten ueberlappt, faellt weg.
 *
 * Ohne das stand "VEREINIGTES KOENIGREICH" quer ueber "ISLE OF MAN" und
 * "Dublin" mitten in "Irische See" (Bilder vom 11.09.2026). Die Vorlage
 * laesst einen Namen lieber ganz weg, als ihn zu ueberschreiben - ein
 * halb lesbarer Name hilft niemandem.
 */
export function ohneUeberdeckung(kaesten: Namenskasten[]): Set<string> {
  const behalten: Namenskasten[] = [];
  const schluessel = new Set<string>();
  for (const kasten of kaesten) {
    const stoert = behalten.some(
      (anderer) =>
        kasten.x < anderer.x + anderer.breite &&
        anderer.x < kasten.x + kasten.breite &&
        kasten.y < anderer.y + anderer.hoehe &&
        anderer.y < kasten.y + kasten.hoehe,
    );
    if (stoert) continue;
    behalten.push(kasten);
    schluessel.add(kasten.schluessel);
  }
  return schluessel;
}
